import axios from "axios";
import { settings } from "@/settings";
import {
  formatDateRecursively,
  getTokenStorage,
  removeAttributesRecursively,
  SESSION_TOKEN_ANONYMOUS_KEY,
} from "@/utils";
import router from "@/router";
import { logger } from "@/utils/logger";
import Notify from 'quasar/src/plugins/Notify.js';;

type AxiosHeadersLike = {
  set?: (key: string, value: string) => void;
  [key: string]: unknown;
};

function setHeader(
  headers: AxiosHeadersLike | undefined,
  key: string,
  value: string
) {
  if (!headers) {
    return;
  }
  if (typeof headers.set === "function") {
    headers.set(key, value);
  } else {
    (headers as Record<string, string>)[key] = value;
  }
}

function rejectUnauthorized(): Promise<never> {
  logger.warn("Unauthorized access attempt - missing session token");
  router.push("/login");
  return Promise.reject(new Error("Unauthorized: missing session token"));
}

function handleUnauthorizedResponse(error: unknown): Promise<never> {
  logger.warn("Unauthorized response - user session invalid");
  router.push("/login");
  return Promise.reject(error);
}

/**
 * Get appropriate timeout based on the request URL
 * Different endpoints have different response times
 */
function getTimeoutForUrl(url?: string): number {
  // Heavy workflow execution endpoints need longer timeout
  if (url && url.includes('/task/execute/')) {
    return 180000; // 3 minutes for workflow task execution
  }
  
  // Other workflow endpoints might also be slow
  if (url && url.includes('/odm-workflow/')) {
    return 120000; // 2 minutes for other workflow operations
  }

  if (
    url &&
    (url.includes('/exportParam/') ||
      url.includes('/importParam/') ||
      url.includes('/paramhistory/') ||
      url.includes('/imports/') ||
      url.includes('/printFile/'))
  ) {
    return 0;
  }
  
  // Default timeout for all other requests
  return 30000; // 30 seconds
}

/**
 * Get user-friendly error message based on error type and status code
 */
function getErrorMessage(error: any): string {
  // If API provided a custom error message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Handle HTTP status codes
  const status = error.response?.status;
  
  if(status)
    {
        switch (status) {
      case 400:
        return 'Données invalides. Veuillez vérifier vos entrées.';
      case 401:
        return 'Vous êtes déconnecté. Veuillez vous reconnecter.';
      case 403:
        return 'Vous n\'avez pas les permissions pour cette action.';
      case 404:
        return 'La ressource demandée n\'existe pas.';
      case 422:
        return 'Les données envoyées ne sont pas valides. Veuillez vérifier.';
      case 429:
        return 'Trop de requêtes. Veuillez attendre quelques secondes.';
      case 500:
      case 502:
      case 503:
        return 'Erreur serveur. Veuillez réessayer dans quelques instants.';
      case 504:
        return 'Le serveur met trop de temps à répondre. Veuillez réessayer.';
      default:
        if (!error.response) {
          return 'Erreur de connexion. Vérifiez votre connexion internet.';
        }
        return error.message || 'Une erreur est survenue. Veuillez réessayer.';
    }
  }else return '';
}

/**
 * Retry logic for failed API requests
 * Exponential backoff: 1s, 2s, 4s, 8s
 */
async function retryRequest(config: any, retryCount = 0): Promise<any> {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second

  if (retryCount >= maxRetries) {
    return Promise.reject(new Error("Max retries exceeded"));
  }

  const delay = baseDelay * Math.pow(2, retryCount);
  
  logger.debug(`Retrying request (attempt ${retryCount + 1}/${maxRetries}) after ${delay}ms`, {
    url: config.url,
  });

  await new Promise(resolve => setTimeout(resolve, delay));
  return axios(config);
}

export function api(daaqResourceUid?: string, anonymousToken?: string) {
  const sessionToken = getTokenStorage();
  const token = anonymousToken
    ? `Bearer ${anonymousToken}`
    : `Bearer ${sessionToken}`;

  const apiInstance = axios.create({
    baseURL: settings.api_url,
    // Timeout will be set dynamically per request in the interceptor
    headers: {
      Authorization: token,
    },
  });

  apiInstance.interceptors.request.use((config) => {
    const hasSessionToken = Boolean(getTokenStorage());
    const hasAnonymousToken = Boolean(anonymousToken);

    if (config.headers && (hasSessionToken || hasAnonymousToken)) {
      setHeader(config.headers, "Authorization", token);
      if (config.method === "post") {
        setHeader(config.headers, "daaqResourceUid", daaqResourceUid || "");
      }
      removeAttributesRecursively(config.data);
      
      // Set appropriate timeout based on endpoint
      config.timeout = getTimeoutForUrl(config.url);
      
      return config;
    }

    return rejectUnauthorized();
  });

  apiInstance.interceptors.response.use(
    (response) => {
      if (
        !response.config?.url?.includes("/exportParam/") &&
        !response.config?.url?.includes("/importParam/") &&
        !response.config?.url?.includes("/paramhistory/") &&
        !(
          response.config?.url?.includes("/imports/") &&
          response.config?.method?.toLowerCase() === "post"
        )
      ) {
        formatDateRecursively(response.data);
      }
      return response;
    },
    async (error) => {
      // Handle 401 Unauthorized (don't show notification, redirect instead)
      if (error.response?.status === 401) {
        return handleUnauthorizedResponse(error);
      }

      // Handle network errors and timeouts with retry
      if (!error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error')) {
        const retryCount = (error.config?._retryCount || 0) + 1;
        
        if (retryCount <= 3) {
          error.config._retryCount = retryCount;
          logger.warn(`Network error - retrying (${retryCount}/3)`, {
            url: error.config?.url,
            error: error.message,
          });
          
          try {
            return await retryRequest(error.config, retryCount - 1);
          } catch (retryError) {
            logger.error("Network request failed after retries", retryError as Error, {
              url: error.config?.url,
              attempts: retryCount,
            });
            
            // Show user notification for failed network request
            const errorMessage = getErrorMessage(retryError);
            if(errorMessage && errorMessage.length > 0){
              Notify.create({
                type: 'negative',
                position: 'top',
                message: errorMessage,
                timeout: 5000,
                actions: [{ label: 'Fermer', color: 'white' }]
              });
            }
            
            return Promise.reject(retryError);
          }
        }
      }

      // Show user notification for all errors (except 401 which was handled above)
      const errorMessage = getErrorMessage(error);
      if(errorMessage && errorMessage.length > 0){
        Notify.create({
          type: 'negative',
          position: 'top',
          message: errorMessage,
          timeout: 5000,
          actions: [{ label: 'Fermer', color: 'white' }]
        });
      }

      // Log all errors for debugging
      if (error.response) {
        logger.error(`API Error (${error.response.status})`, error.message, {
          url: error.config?.url,
          status: error.response.status,
          statusText: error.response.statusText,
        });
      } else {
        logger.error("API Request Failed", error.message, {
          url: error.config?.url,
          error: error.message,
        });
      }

      return Promise.reject(error);
    }
  );

  return apiInstance;
}

export function apiAnonymousToken(daaqResourceUid?: string) {
  const token = `Bearer ${localStorage.getItem(SESSION_TOKEN_ANONYMOUS_KEY)}`;

  const apiInstance = axios.create({
    baseURL: settings.api_url,
    // Timeout will be set dynamically per request in the interceptor
    headers: {
      Authorization: token,
    },
  });

  apiInstance.interceptors.request.use((config) => {
    const hasAnonymousToken = Boolean(
      localStorage.getItem(SESSION_TOKEN_ANONYMOUS_KEY)
    );

    if (config.headers && hasAnonymousToken) {
      setHeader(config.headers, "Authorization", token);
      if (config.method === "post") {
        setHeader(config.headers, "daaqResourceUid", daaqResourceUid || "");
      }
      removeAttributesRecursively(config.data);
      
      // Set appropriate timeout based on endpoint
      config.timeout = getTimeoutForUrl(config.url);
      
      return config;
    }

    return rejectUnauthorized();
  });

  apiInstance.interceptors.response.use(
    (response) => {
      if (
        !response.config?.url?.includes("/exportParam/") &&
        !response.config?.url?.includes("/importParam/") &&
        !response.config?.url?.includes("/paramhistory/") &&
        !(
          response.config?.url?.includes("/imports/") &&
          response.config?.method?.toLowerCase() === "post"
        )
      ) {
        formatDateRecursively(response.data);
      }
      return response;
    },
    async (error) => {
      // Handle 401 Unauthorized (don't show notification, redirect instead)
      if (error.response?.status === 401) {
        return handleUnauthorizedResponse(error);
      }

      // Handle network errors and timeouts with retry
      if (!error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error')) {
        const retryCount = (error.config?._retryCount || 0) + 1;
        
        if (retryCount <= 3) {
          error.config._retryCount = retryCount;
          logger.warn(`Network error - retrying (${retryCount}/3)`, {
            url: error.config?.url,
            error: error.message,
          });
          
          try {
            return await retryRequest(error.config, retryCount - 1);
          } catch (retryError) {
            logger.error("Network request failed after retries", retryError as Error, {
              url: error.config?.url,
              attempts: retryCount,
            });
            
            // Show user notification for failed network request
            const errorMessage = getErrorMessage(retryError);
            if(errorMessage && errorMessage.length > 0){
              Notify.create({
                type: 'negative',
                position: 'top',
                message: errorMessage,
                timeout: 5000,
                actions: [{ label: 'Fermer', color: 'white' }]
              });
            }
            
            return Promise.reject(retryError);
          }
        }
      }

      // Show user notification for all errors (except 401 which was handled above)
      const errorMessage = getErrorMessage(error);
      Notify.create({
        type: 'negative',
        position: 'top',
        message: errorMessage,
        timeout: 5000,
        actions: [{ label: 'Fermer', color: 'white' }]
      });

      // Log all errors for debugging
      if (error.response) {
        logger.error(`API Error (${error.response.status})`, error.message, {
          url: error.config?.url,
          status: error.response.status,
          statusText: error.response.statusText,
        });
      } else {
        logger.error("API Request Failed", error.message, {
          url: error.config?.url,
          error: error.message,
        });
      }

      return Promise.reject(error);
    }
  );

  return apiInstance;
}
